// Helpers for service-callback orders (order_type === 'service').
//
// Service lines live on the canonical top-level `services` array (mirroring the
// public.services relationship); each line is
// `{ code, type, description, quantity, size, ... }`. Used to detect
// service-only orders and render the read-only Service Callback view.

import { effectiveQty, serviceCodeKey } from './serviceLines';

export const isServiceCallbackOrder = (order) => order?.order_type === 'service';

const hasOrderCatalogScope = (order) =>
    !!order?.service_catalog_filter &&
    typeof order.service_catalog_filter === 'object' &&
    Object.keys(order.service_catalog_filter).length > 0;

export const customerHasServiceDefinitions = (order, serviceCatalog) =>
    (Array.isArray(serviceCatalog?.services) && serviceCatalog.services.length > 0) ||
    hasOrderCatalogScope(order) ||
    isServiceCallbackOrder(order) ||
    (Array.isArray(order?.services) && order.services.length > 0);

// Raw service lines off the canonical top-level `services` array. No fallback
// to `custom_fields.general_electric.services` — `services` is the sole source,
// so an order queried without the relationship reads as zero lines.
const getRawServiceLines = (order) => (Array.isArray(order?.services) ? order.services : []);

// Normalize raw service rows into the OrderCard / Services panel display shape.
// Synthetic service_id per index keeps React keys stable when a line lacks one.
const normalizeServiceLines = (order, rawLines) =>
    rawLines.map((line, idx) => ({
        service_id: line.service_id || `${order?.order_id || 'order'}-svc-${idx}`,
        code: line.code,
        type: line.type,
        description: line.description,
        quantity: line.quantity,
        size: line.size,
        service_time: line.service_time,
        metadata: line.metadata,
        // GE passthrough fields live in metadata jsonb; fall back to the
        // top-level key when metadata is absent.
        customer_tracking_number: line.metadata?.customer_tracking_number ?? line.customer_tracking_number,
    }));

// Normalized service lines for any order that carries them, regardless of
// `order_type` (including mixed delivery+services orders). Use this rather than
// the service-callback-gated `getServiceCallbackLines` for non-service orders.
export const getServiceLines = (order) => normalizeServiceLines(order, getRawServiceLines(order));

// Returns lines only when `order_type === 'service'`. Mixed delivery+services
// orders should call `getServiceLines` instead.
export const getServiceCallbackLines = (order) => {
    if (!isServiceCallbackOrder(order)) return [];
    return normalizeServiceLines(order, getRawServiceLines(order));
};

// Collapse duplicate service lines (same canonical code key) into one display row
// with the summed quantity. Preserves first-seen order and keeps the first
// occurrence's descriptive fields + service_id so React keys stay stable.
// `count` is the number of underlying rows folded together. Note `code` is the
// first occurrence's RAW code, so consumers matching it against a line must
// re-key through serviceCodeKey rather than compare strings.
export const groupServiceLinesByCode = (lines) => {
    const order = [];
    const byCode = new Map();
    (lines || []).forEach((line) => {
        const key = serviceCodeKey(line?.code);
        if (!byCode.has(key)) {
            byCode.set(key, {
                service_id: line.service_id,
                code: line.code,
                type: line.type,
                description: line.description,
                size: line.size,
                service_time: line.service_time,
                quantity: 0,
                count: 0,
            });
            order.push(key);
        }
        const group = byCode.get(key);
        group.quantity += effectiveQty(line);
        group.count += 1;
    });
    return order.map((key) => byCode.get(key));
};

// True when this is the last line on a service-callback order — used to disable
// the per-row Delete (removing it would orphan a service order with an empty
// `services` array, which the save gate rejects). Only fires for service-callback
// orders; mixed orders stay valid with zero services. `lines` is the normalized
// array rendered in the panel; undefined/null counts as zero.
export const isLastServiceCallbackLine = (order, lines) =>
    isServiceCallbackOrder(order) && (lines?.length ?? 0) === 1;

// Visit service time in MINUTES from the generic order-level `service_time` column
// (the parser normalizes it per source; for GE, the work-order appointment_duration).
// Returns null when absent.
export const getServiceCallbackServiceTime = (order) => {
    if (!isServiceCallbackOrder(order)) return null;
    const raw = order?.service_time;
    const parsed = typeof raw === 'string' ? Number(raw) : raw;
    return typeof parsed === 'number' && parsed > 0 ? Math.round(parsed) : null;
};
